/**
 * La carta d'ingresso: un foglio chiaro, e — sulla pagina d'accesso — il
 * fianco scuro del prodotto accanto (ADR-009).
 *
 * **Non e' un eroe di marketing.** Il fianco dice in una frase che cosa fa il
 * prodotto e disegna la traccia di un volo con un riquadro: sono le due cose
 * che chi entra vedra' dentro. Nessun numero inventato — un dato che non
 * arriva dal servizio non si scrive (§ 02). Il colore e' dato, non
 * decorazione: l'eyebrow porta l'accento perche' e' l'unica etichetta di
 * sistema, e la primaria e' una.
 *
 * Il cambio della parola d'ordine usa la stessa carta senza fianco: sta
 * dentro la Carta, e li' il prodotto e' gia' attorno.
 */
import type { ReactNode } from 'react'

import { useT } from '../i18n/LocaleProvider'
import { Mark } from './Mark'

export function LoginCard({
  title,
  eyebrow = 'ViaTecnologia',
  brand = false,
  children,
}: {
  title: string
  eyebrow?: string
  /** Con il fianco scuro del prodotto: solo sulla pagina d'accesso. */
  brand?: boolean
  children: ReactNode
}): JSX.Element {
  return (
    <main className={brand ? 'ingresso ingresso-carta ingresso-doppio' : 'ingresso ingresso-carta'}>
      {brand && <Brand />}
      <div className="ingresso-modulo">
        {brand && <Filigrana />}
        <div className="login-card">
          <p className="t-label login-card-eyebrow">{eyebrow}</p>
          <h1 className="t-object login-card-titolo">
            <Mark size={30} />
            {title}
          </h1>
          {children}
        </div>
      </div>
    </main>
  )
}

/** La filigrana del lato chiaro: il reticolo della carta e il segno, grande e
 *  appena visibile, che spunta dall'angolo in basso a destra. Serve quando il
 *  fianco scuro non c'e' — sotto i 900 px — perche' la pagina non sia un
 *  modulo su un foglio vuoto; ma sta bene anche accanto. Decorativa: gli
 *  strumenti di lettura la saltano. */
function Filigrana(): JSX.Element {
  return (
    <svg className="ingresso-filigrana" viewBox="0 0 800 800" preserveAspectRatio="xMaxYMax slice" aria-hidden="true">
      <defs>
        <pattern id="ingresso-reticolo" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M40 0H0V40" fill="none" stroke="var(--line-subtle)" />
        </pattern>
      </defs>
      <rect width="800" height="800" fill="url(#ingresso-reticolo)" />
      <g className="ingresso-filigrana-segno" transform="translate(360 360) scale(9.5)">
        <path d="M45.20 18.33A19 19 0 1 0 50.65 35.62" fill="none" strokeWidth="6.5" strokeLinecap="round" />
        <path d="M50.65 35.62Q41 40.5 31 35.8" fill="none" strokeWidth="6.5" strokeLinecap="round" />
        <circle cx="31" cy="35.8" r="4.9" />
      </g>
    </svg>
  )
}

function Brand(): JSX.Element {
  const t = useT()
  return (
    <section data-theme="cockpit" className="login-brand" aria-label={t('login.brandLabel')}>
      {/* La traccia di un volo e un riquadro con il suo cartellino: i colori
          sono quelli dei disegni sopra il fotogramma (§ 08), che non seguono
          il contorno — qui, come sul banco. */}
      <svg className="login-brand-sfondo" viewBox="0 0 800 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <pattern id="login-reticolo" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="var(--rail-line)" />
          </pattern>
        </defs>
        <rect width="800" height="900" fill="url(#login-reticolo)" />
        <path
          d="M-20 700 C 150 600, 260 420, 420 380 S 700 300, 840 120"
          fill="none"
          stroke="var(--overlay-reference)"
          strokeWidth="1.5"
          strokeDasharray="5 7"
          opacity="0.8"
        />
        <path
          d="M-20 760 C 160 680, 300 520, 470 470 S 720 400, 840 260"
          fill="none"
          stroke="var(--overlay-reference)"
          strokeWidth="1"
          opacity="0.3"
        />
        <g fill="var(--overlay-reference)">
          <circle cx="420" cy="380" r="4" />
          <circle cx="260" cy="470" r="3" />
          <circle cx="620" cy="300" r="3" />
        </g>
        <g fill="none" stroke="var(--overlay-active)" strokeWidth="1.5">
          <path d="M610 290h20M620 280v20" />
          <rect x="600" y="280" width="40" height="40" rx="2" opacity="0.6" />
        </g>
        <text x="632" y="272" className="login-brand-cartellino" fill="var(--overlay-active)">
          D60 · Δ −0,19
        </text>
      </svg>
      <p className="login-brand-marchio">
        <Mark size={34} />
        <span>
          Grid<span className="login-brand-fly">Fly</span>
        </span>
        <span className="tenue">· Via Tecnologia</span>
      </p>
      <div className="login-brand-testo">
        <p className="login-brand-claim">
          {t('login.claimBefore')}
          <br />
          <span>{t('login.claimAccent')}</span>
          <br />
          {t('login.claimAfter')}
        </p>
        <p className="login-brand-lead">{t('login.lead')}</p>
      </div>
      <p className="login-brand-piede t-num">{t('login.footer')}</p>
    </section>
  )
}
